import { ConfigurationType } from 'data/types';

export function getNextConfigurationName(
  baseName: string,
  configurations: ConfigurationType[]
): string {
  const existingNames = new Set(
    configurations.map((configuration) => configuration.name)
  );
  if (!existingNames.has(baseName)) return baseName;

  let suffix = 2;
  while (existingNames.has(`${baseName} ${suffix}`)) {
    suffix += 1;
  }
  return `${baseName} ${suffix}`;
}

export function getNextCustomConfigurationName(
  configurations: ConfigurationType[]
): string {
  return getNextConfigurationName('Custom Configuration', configurations);
}

export function isUserConfigurationInProgress(
  configuration: ConfigurationType | undefined
): boolean {
  if (!configuration) return false;
  return configuration.modified === true || !configuration.aiGenerated;
}

export function hasUserConfigurationWork(
  configuration: ConfigurationType | undefined
): boolean {
  if (!configuration) return false;
  if (configuration.modified === true) return true;
  return !configuration.aiGenerated && configuration.condensers.length > 0;
}

export function collectProtectedConfigurationIds(
  configurations: ConfigurationType[],
  additionalProtectedIds: string[]
): string[] {
  const protectedIds = new Set(additionalProtectedIds);
  configurations.forEach((configuration) => {
    if (isUserConfigurationInProgress(configuration)) {
      protectedIds.add(configuration.uniqueId);
    }
  });
  return [...protectedIds];
}

export function preserveLocalUserConfigurations(
  incomingConfigurations: ConfigurationType[],
  existingConfigurations: ConfigurationType[],
  protectedConfigurationIds: string[]
): ConfigurationType[] {
  const protectedIds = new Set(protectedConfigurationIds);
  const existingById = new Map(
    existingConfigurations.map((configuration) => [
      configuration.uniqueId,
      configuration
    ])
  );
  const mergedConfigurations = incomingConfigurations.flatMap(
    (incomingConfiguration) => {
      if (!protectedIds.has(incomingConfiguration.uniqueId)) {
        return [incomingConfiguration];
      }
      const existingConfiguration = existingById.get(
        incomingConfiguration.uniqueId
      );
      return existingConfiguration ? [existingConfiguration] : [];
    }
  );
  const mergedIds = new Set(
    mergedConfigurations.map((configuration) => configuration.uniqueId)
  );
  const localOnlyConfigurations = existingConfigurations.filter(
    (configuration) =>
      protectedIds.has(configuration.uniqueId) &&
      !mergedIds.has(configuration.uniqueId)
  );

  return [...mergedConfigurations, ...localOnlyConfigurations];
}

export function resolveActiveConfigurationIndex(
  nextConfigurations: ConfigurationType[],
  previousActiveConfiguration: ConfigurationType | undefined
): number {
  if (!previousActiveConfiguration) return 0;

  const nextIndex = nextConfigurations.findIndex(
    (configuration) =>
      configuration.uniqueId === previousActiveConfiguration.uniqueId
  );
  if (nextIndex >= 0) return nextIndex;

  if (previousActiveConfiguration.aiGenerated) {
    const firstAiConfigIndex = nextConfigurations.findIndex(
      (configuration) => configuration.aiGenerated
    );
    if (firstAiConfigIndex >= 0) return firstAiConfigIndex;
  }

  return 0;
}

export function shouldKeepActiveConfigurationAfterGeneration(
  previousConfigurationUniqueId: string | undefined,
  activeConfiguration: ConfigurationType | undefined
): boolean {
  if (!activeConfiguration) return false;
  return (
    activeConfiguration.uniqueId !== previousConfigurationUniqueId ||
    hasUserConfigurationWork(activeConfiguration)
  );
}
